import { CalendarOutlined, FileDoneOutlined, FileTextOutlined, ShopOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CREATE_jwel } from "../../../Config/Config";

/* ── animation variants ── */
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.45, ease: "easeOut" },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

/* ── Stat card — compact ── */
const StatCard = ({ icon, title, value, accent, index }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    whileHover={{ y: -3, boxShadow: `0 8px 20px ${accent}28` }}
    style={{
      background: "#fff",
      borderRadius: 10,
      padding: "8px 8px 7px",
      textAlign: "center",
      boxShadow: "0 1px 8px rgba(44,27,105,0.07)",
      cursor: "default",
      border: `1px solid ${accent}1a`,
      position: "relative",
      overflow: "hidden",
      transition: "box-shadow 0.2s",
    }}
  >
    {/* Glow blob */}
    <div style={{
      position: "absolute", top: -12, right: -12,
      width: 40, height: 40, borderRadius: "50%",
      background: `${accent}38`, pointerEvents: "none",
    }} />

    {/* Icon bubble */}
    <div style={{
      width: 28, height: 28, borderRadius: 8,
      background: `${accent}14`,
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto 5px",
      fontSize: 13, color: accent,
    }}>
      {icon}
    </div>

    <div style={{ fontSize: 9, color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1.2 }}>
      {title}
    </div>
    <div style={{ fontSize: 12, fontWeight: 800, color: "#2d1b69", marginTop: 2, lineHeight: 1.2 }}>
      {value}
    </div>
  </motion.div>
);

/* ── small tag chip ── */
const Tag = ({ children, color = "#595656", bg = "#f0eaff", size = 10, weight = 700 }) => (
  <span style={{
    fontSize: size, color, fontWeight: weight,
    background: bg, borderRadius: 5, padding: "1px 6px",
    whiteSpace: "nowrap",
  }}>
    {children}
  </span>
);

/* ════════════════════════════════════════════════════════════════
   TODAY'S SALES (data layer)
   Source: CREDIT_BILLMAST
   where : CAST(BILLDATE AS DATE) = 'today' AND CANCELL = 'NO'
   Sales amount = sum of NETAMT, Bills = number of rows returned.
   ════════════════════════════════════════════════════════════════ */
const SALES_SOURCE = {
  endpoint: "/api/ERP/GetDataFromGivenTableNamewithwhere",
  table: "CREDIT_BILLMAST",
  dateKey: "BILLDATE",
  amountKey: "NETAMT",
  cancelKey: "CANCELL",
};

const FirstColumn = () => {
  const [firmData, setFirmData] = useState(null);
  const [firmError, setFirmError] = useState(null);
  const [totalInvoice, setTotalInvoice] = useState(0);
  const [saleAmount, setSaleAmount] = useState(0);

  const image = localStorage.getItem("singleImage");
  const fYear = localStorage.getItem("fYear");
  const tenantName = localStorage.getItem("tenantName");

  /* ── Firm configure ── */
  useEffect(() => {
    if (!tenantName) {
      console.warn("tenantName missing in localStorage");
      setFirmError("Tenant not found");
      return;
    }

    // Show cached firm data instantly (if any)
    try {
      const cached = JSON.parse(localStorage.getItem(`firmData_${tenantName}`) || "null");
      if (cached) setFirmData(cached);
    } catch { /* ignore bad cache */ }

    let cancelled = false;
    const tableNames = ["FIRM_CONFIGURE", "Firm_Configure"];

    const load = async (attempt = 0) => {
      const table = tableNames[attempt % tableNames.length];
      try {
        const res = await axios.get(
          `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=${table}`,
          { headers: { tenantName } },
        );
        const d = Array.isArray(res.data) ? res.data[0] : res.data;
        if (!d) throw new Error("Empty firm response");
        if (cancelled) return;
        setFirmData(d);
        setFirmError(null);
        localStorage.setItem(`firmData_${tenantName}`, JSON.stringify(d));
      } catch (err) {
        console.error(`Firm fetch failed (${table}, try ${attempt + 1})`, err?.response?.status, err);
        if (cancelled) return;
        if (attempt < 4) {
          setTimeout(() => load(attempt + 1), 1000 * (attempt + 1)); // retry with backoff
        } else {
          setFirmError("Failed to load firm details");
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [tenantName]);

  /* ── Today's sales amount + bills count ── */
  useEffect(() => {
    const src = SALES_SOURCE;
    const today = dayjs().format("YYYY-MM-DD");
    // Same filter as the API URL you shared (axios encodes it for us)
    const where = `CAST(${src.dateKey} AS DATE) = '${today}' AND ${src.cancelKey} = 'NO'`;

    let ignore = false;
    axios
      .get(`${CREATE_jwel}${src.endpoint}`, {
        params: { tableName: src.table, where },
        headers: { tenantName },
      })
      .then((res) => {
        if (ignore) return;
        // Extra safety: drop any cancelled row that slips through
        const rows = (Array.isArray(res.data) ? res.data : []).filter(
          (r) => String(r[src.cancelKey] ?? "").toUpperCase() !== "YES",
        );
        setSaleAmount(rows.reduce((a, r) => a + (Number(r[src.amountKey]) || 0), 0));
        setTotalInvoice(rows.length);
      })
      .catch((err) => {
        console.error("Today's sales fetch failed:", err?.response?.status, err);
        if (ignore) return;
        setSaleAmount(0);
        setTotalInvoice(0);
      });

    return () => { ignore = true; };
  }, [tenantName]);

  /* ── Total bills for fYear (fire-and-forget) ── */
  useEffect(() => {
    if (!fYear || fYear === "null") return;
    fetch(`${CREATE_jwel}/api/DashBoard/GetTotalBills?fYear=${fYear}&saleCode=1`, {
      headers: { tenantName },
    }).catch(console.error);
  }, [fYear, tenantName]);

  const fmtAmt = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  /* ── Derived firm values ── */
  const address = [firmData?.ADD1, firmData?.ADD2]
    .map((s) => (s || "").trim())
    .filter((s) => s && s !== "-")
    .join(" ");

  const taxLabel = firmData?.COMPOSITIONDLR
    ? "Composition"
    : firmData?.GST
      ? "GST"
      : "Regular";

  const stats = [
    {
      icon: <CalendarOutlined />,
      title: "Fin. Year",
      value: firmData?.FYEAR || fYear || "—",
      accent: "#43aa8b",
    },
    {
      icon: <FileTextOutlined />,
      title: "Today Sales",
      value: `₹${fmtAmt(saleAmount)}`,
      accent: "#4cc9f0",
    },
    {
      icon: <FileDoneOutlined />,
      title: "Bills Today",
      value: totalInvoice,
      accent: "#f94144",
    },
  ];

  return (
    <motion.div
      variants={scaleIn}
      initial="hidden"
      animate="visible"
      style={{
        borderRadius: 14,
        background: "linear-gradient(145deg,#f5f0ff 0%,#e8d7ff 100%)",
        boxShadow: "0 4px 20px rgba(44,27,105,0.10)",
        padding: "12px 12px 10px",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ── Background decoration ── */}
      <div style={{
        position: "absolute", top: -30, right: -30,
        width: 100, height: 100, borderRadius: "50%",
        background: "linear-gradient(135deg,#b8a4e840,#4c349430)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: -20, left: -20,
        width: 70, height: 70, borderRadius: "50%",
        background: "linear-gradient(135deg,#4cc9f030,#43aa8b25)",
        pointerEvents: "none",
      }} />

      {/* ── Firm header ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}
      >
        {/* Left — firm info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: "linear-gradient(135deg,#2d1b69,#7c5cbf)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <ShopOutlined style={{ fontSize: 11, color: "#fff" }} />
            </div>
            <div
              title={firmData?.FIRMNAME}
              style={{
                fontSize: 18, fontWeight: 800, color: "#2d1b69",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
            >
              {firmData?.FIRMNAME || (firmError ? "—" : "Loading...")}
            </div>
          </div>

          {/* Branch + address */}
          {(firmData?.BRANCHNAME || address) && (
            <div style={{ fontSize: 11, color: "#6b5b9a", fontWeight: 600, lineHeight: 1.4, marginBottom: 6 }}>
              {firmData?.BRANCHNAME && (
                <div style={{ color: "#2d1b69", fontWeight: 700 }}>{firmData.BRANCHNAME}</div>
              )}
              {address && <div>{address}</div>}
            </div>
          )}

          {/* Error message */}
          {firmError && (
            <div style={{ fontSize: 10, color: "#f94144", fontWeight: 600, marginBottom: 6 }}>
              {firmError}
            </div>
          )}

          {/* Tags */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 6px" }}>
            <Tag color="#7c5cbf" bg="#f0eaff" size={11} weight={800}>
              📍 {firmData?.CITY || "City"}
            </Tag>
            {firmData?.FMOBILE && (
              <Tag bg="#eef6ff">📞 {firmData.FMOBILE}</Tag>
            )}
            {firmData?.OWNERNAME && (
              <Tag bg="#fff7e6">👤 {firmData.OWNERNAME}</Tag>
            )}
            <Tag bg="#f0fff4">
              {taxLabel}
              {firmData?.TINNO ? `: ${firmData.TINNO}` : ""}
            </Tag>
          </div>
        </div>

        {/* Right — logo */}
        <Popover
          content={
            image
              ? <img src={image} alt="logo" style={{ maxWidth: 180, borderRadius: 8 }} />
              : <div style={{ padding: 10, color: "#aaa" }}>No image</div>
          }
          trigger="click"
        >
          <motion.div
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            style={{
              width: 42, height: 42, borderRadius: 11, cursor: "pointer",
              background: image ? "transparent" : "linear-gradient(135deg,#b8a4e8,#4c3494)",
              boxShadow: "0 3px 10px rgba(44,27,105,0.18)",
              overflow: "hidden", flexShrink: 0, marginLeft: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {image
              ? <img src={image} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <ShopOutlined style={{ fontSize: 18, color: "#fff" }} />
            }
          </motion.div>
        </Popover>
      </motion.div>

      {/* ── Divider ── */}
      <div style={{
        height: 1,
        background: "linear-gradient(90deg,#b8a4e840,#4c349420,transparent)",
        marginBottom: 5,
        marginTop: 10,
      }} />

      {/* ── Stat cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, marginTop: 15 }}>
        {stats.map((s, i) => (
          <StatCard key={i} {...s} index={i} />
        ))}
      </div>
    </motion.div>
  );
};

export default FirstColumn;